import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { appSettingsKeys } from "@/lib/query";
import type { AppSettingsUpdateInput } from "@/schemas/app-settings";

/** Which optional sections of the app are switched on for this workspace. */
export interface AppSettings {
  /** Performance Feedback & Reviews (DM-WF-001). Off until the January rollout. */
  reviewsEnabled: boolean;
  /** Divert every outbound email to one address — Settings → Config → Email testing. */
  testEmailEnabled: boolean;
  testEmailAddress: string | null;
}

/**
 * Read the workspace's feature flags.
 *
 * Every signed-in user reads this — the sidebar needs it to decide whether to
 * render the Reviews item. It is not a security boundary: the server refuses
 * every `/api/reviews*` route with a 404 when the flag is off, and that is the
 * check that counts.
 */
export function useAppSettings() {
  return useQuery({
    queryKey: appSettingsKeys.all,
    queryFn: async (): Promise<AppSettings> => {
      const res = await fetch("/api/app-settings");
      if (!res.ok) throw new Error("Failed to fetch feature availability");
      const json = await res.json();
      return json.data;
    },
  });
}

/** Turn a feature on or off. Admin-only server-side; partial, so send one flag. */
export function useUpdateAppSettings() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: AppSettingsUpdateInput): Promise<AppSettings> => {
      const res = await fetch("/api/app-settings", {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(input),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => null);
        throw new Error(body?.error ?? "Update failed");
      }
      const json = await res.json();
      return json.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: appSettingsKeys.all });
      toast.success("Settings updated");
    },
    onError: (e: Error) => toast.error(e.message),
  });
}
